import { WorkmateLoader } from '@/components/ui/workmate-loader';
import { Component, type ErrorInfo, type ReactNode } from 'react';
import { Button } from '@/components/ui/button';
import {
  captureApplicationError,
  forceAssetRecoveryReload,
  isRecoverableAssetLoadError,
  recoverFromAssetLoadError,
} from '@/lib/monitoring';
import { appLogger } from '@/lib/logger';

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
  assetRecoveryTimedOut: boolean;
}

export class ErrorBoundary extends Component<Props, State> {
  private assetReloadFallbackTimer: number | null = null;

  constructor(props: Props) {
    super(props);
    this.state = { hasError: false, error: null, assetRecoveryTimedOut: false };
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error, assetRecoveryTimedOut: false };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    appLogger.error('ErrorBoundary caught an error:', error, errorInfo);
    if (recoverFromAssetLoadError(error)) {
      this.scheduleAssetReloadFallback();
      return;
    }

    if (isRecoverableAssetLoadError(error)) {
      this.setState({ assetRecoveryTimedOut: true });
      return;
    }

    captureApplicationError(error, {
      tags: { boundary: 'react' },
      extra: { componentStack: errorInfo.componentStack },
    });
  }

  componentWillUnmount() {
    this.clearAssetReloadFallback();
  }

  private scheduleAssetReloadFallback() {
    if (typeof window === 'undefined') return;

    this.clearAssetReloadFallback();
    this.assetReloadFallbackTimer = window.setTimeout(() => {
      if (this.state.hasError && isRecoverableAssetLoadError(this.state.error)) {
        this.setState({ assetRecoveryTimedOut: true });
      }
    }, 4500);
  }

  private clearAssetReloadFallback() {
    if (this.assetReloadFallbackTimer !== null) {
      window.clearTimeout(this.assetReloadFallbackTimer);
      this.assetReloadFallbackTimer = null;
    }
  }

  render() {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback;
      }

      if (isRecoverableAssetLoadError(this.state.error)) {
        const recoveryTimedOut = this.state.assetRecoveryTimedOut;
        return (
          <div className="flex min-h-[50vh] flex-col items-center justify-center gap-4 p-8 text-center">
            <WorkmateLoader
              label={
                recoveryTimedOut ? 'WorkLynx could not finish loading' : 'Reopening WorkLynx...'
              }
              paused={recoveryTimedOut}
              compact
            />
            <p className="max-w-md text-sm text-muted">
              {recoveryTimedOut
                ? 'A page file could not be downloaded. Check your connection and try reloading. Your page address and filters will be kept.'
                : 'A page file could not be downloaded. Trying a fresh copy now.'}
            </p>
            <Button onClick={() => forceAssetRecoveryReload('asset-load-manual')}>
              Reload WorkLynx
            </Button>
          </div>
        );
      }

      return (
        <div className="flex min-h-[50vh] flex-col items-center justify-center gap-4 p-8 text-center">
          <div className="rounded-full bg-danger-soft p-4">
            <svg
              className="h-8 w-8 text-danger"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth={2}
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z"
              />
            </svg>
          </div>
          <h2 className="text-xl font-semibold text-foreground">Something went wrong</h2>
          <p className="max-w-md text-sm text-muted">
            {this.state.error?.message || 'An unexpected error occurred.'}
          </p>
          <Button
            onClick={() => {
              this.setState({
                hasError: false,
                error: null,
                assetRecoveryTimedOut: false,
              });
              window.location.reload();
            }}
          >
            Reload Page
          </Button>
        </div>
      );
    }

    return this.props.children;
  }
}
